import React, { useState, useCallback, useEffect } from 'react';
import { routes } from 'constants/config';
import { useFetch, useFeatureFlags } from 'hooks';
import request from 'utils/request_ajax';
import AlertBox from 'components/shared/AlertBox';
import Spinner from 'ui/Spinner';

const Regenerate = ({
  subdomain,
  zendeskSecurityKey,
  regenerateZendeskKey,
}) => {
  return (
    <div className="SettingsContent-subSection">
      <p>
        <label htmlFor="subdomain-label" className="fw-b">
          {t('settings.tab.integrations.zendesk.subdomain_label')}
        </label>

        <input
          id="subdomain-label"
          className="form-control"
          value={subdomain}
          readOnly
        />
      </p>
      <p>
        <label htmlFor="zendesk_security_key" className="fw-b">
          {t('settings.tab.integrations.zendesk.security_key_label')}
        </label>

        <input
          id="zendesk-security-key"
          className="form-control"
          value={zendeskSecurityKey}
          aria-label={t('settings.tab.integrations.zendesk.security_key_label')}
          readOnly
        />
      </p>
      <button
        id="zendesk-regenerate-key"
        type="button"
        className="Button--customColor NewButton"
        onClick={regenerateZendeskKey}
      >
        {t('settings.tab.integrations.zendesk.regenerate_key')}
      </button>
    </div>
  );
};

const Generate = ({ generateZendeskKey }) => {
  return (
    <div className="SettingsContent-subSection">
      <p>
        {t('settings.tab.integrations.zendesk.generation_explanation_html')}
      </p>
      <button
        id="zendesk-generate-key"
        type="button"
        className="Button--customColor NewButton"
        onClick={generateZendeskKey}
      >
        {t('settings.tab.integrations.zendesk.generate_key')}
      </button>
    </div>
  );
};

const ZendeskIntegrationSettings = props => {
  const [subdomain, setSubdomain] = useState('');
  const [zendeskSecurityKey, setZendeskSecurityKey] = useState('');
  const [showAlert, setShowAlert] = useState(false);

  const { data, loading, error } = useFetch(routes.SETTINGS.INTEGRATIONS);
  const hasLearningRebrandFlag = useFeatureFlags(['learning-rebrand']);

  const generateZendeskKey = useCallback(async () => {
    const result = await request.putJSON(
      routes.SETTINGS.GENERATE_ZENDESK_SECURITY_KEY,
    );

    setZendeskSecurityKey(result.zendeskSecurityKey);
  }, []);

  const regenerateZendeskKey = useCallback(async () => {
    if (
      /* eslint-disable-next-line no-alert */
      window.confirm(
        t('settings.tab.integrations.zendesk.confirm_regenerate_key'),
      )
    ) {
      const result = await request.putJSON(
        routes.SETTINGS.GENERATE_ZENDESK_SECURITY_KEY,
      );
      setZendeskSecurityKey(result.zendeskSecurityKey);
    }
  }, []);

  useEffect(() => {
    if (error) {
      setShowAlert(true);
    }
    if (data?.zendeskSecurityKey) {
      setZendeskSecurityKey(data.zendeskSecurityKey);
    }
    if (data?.subdomain) {
      setSubdomain(data.subdomain);
    }
  }, [loading, data, error]);

  return (
    <>
      {showAlert && (
        <AlertBox onDismiss={() => setShowAlert(false)}>
          {t('settings.tab.integrations.zendesk.error_please_refresh')}
        </AlertBox>
      )}

      <div className="row" id="zendesk-integration">
        <div className="col-md-12">
          <div className="SettingsContent-section">
            <h3 className="SettingsContent-heading">
              {t('settings.tab.integrations.zendesk.title')}
            </h3>
            <hr />
            {hasLearningRebrandFlag ? (
              <p>
                {t(
                  'settings.tab.integrations.zendesk.install_instructions_learning',
                )}
              </p>
            ) : (
              <p>
                {t('settings.tab.integrations.zendesk.install_instructions')}
              </p>
            )}
            {/* eslint-disable-next-line no-nested-ternary */}
            {loading ? (
              <Spinner />
            ) : zendeskSecurityKey ? (
              <Regenerate
                zendeskSecurityKey={zendeskSecurityKey}
                subdomain={subdomain}
                regenerateZendeskKey={regenerateZendeskKey}
              />
            ) : (
              <Generate generateZendeskKey={generateZendeskKey} />
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default ZendeskIntegrationSettings;
